import {EventDispatcher} from './EventDispatcher.js';
import {
  CHANGE
} from './constants.js';
import {isNil} from './utils.js';

/**
 * Transitions an array of integers from one value to another over the specified duration.
 * This utility class is very specifically designed to meet the needs of MegaScreen's survey-bar
 * percentages, as follows:
 *
 * -the animateTo() method automatically starts from zero when no starting point is provided
 * -when an animation is interrupted with a new animation, the animateTo() method automatically
 * begins the next animation where the previous animation left off.
 *
 * Register for CHANGE events to retrieve the current values of the transitioning integers.
 * To "jump" to a specific set of numbers without animating, use skipTo().
 */
export class NumberAnimator extends EventDispatcher {
  constructor () {
    super();
    this.intervalID = -1;
    // Hard-code the duration of the animation. This value is also hard-coded
    // in the CSS for the survey-bar foreground. In theory, in the future, both could be converted
    // to Editor-configurable settings.
    this.duration = 1000;
    this.startTime = -1;
    // The values of the transitioning numbers during the animation.
    this.current = [];
    // The list of numbers from which to start the animations (e.g., [32, 1, 67])
    this.from = null;
    // The list of numbers to which the animations should transition (e.g., [34, 0, 66])
    this.to = null;
    // The most recent value of this.to.
    this.previousTo = null;
  }

  /**
   * Internal. Specifies the numbers from which the animation should transition.
   */
  setFrom (from = []) {
    this.from = from;
  }

  /**
   * Internal. Specifies the numbers to which the animation should transition.
   */
  setTo (to = []) {
    this.to = to;
  }

  /**
   * Starts an animation from "from" to "to". During the animation, NumberAnimator dispatches
   * CHANGE events from which listeners can retrieve the current transitioning values.
   *
   * @param to An array of numbers to which the animations should transition (e.g., [34, 0, 66]).
   */
  animateTo (to) {
    // If the target "to" has not changed, don't start the animation
    if (JSON.stringify(this.previousTo) === JSON.stringify(to)) {
      return;
    }

    // If from isn't provided, set it to an array of zeros matching the length of this.to.
    if (isNil(this.from)) {
      this.setFrom(Array(to.length).fill(0));
    }

    // If a previous animation is still in progress, start the next animation where the previous
    // animation left off. Use the spread operator to copy values from this.current to a new array.
    if (this.isRunning()) {
      this.setFrom([...this.current]);
    }

    // If the new "to" array's length doesn't match the most recent "from" array's length, truncate
    // or pad the "from" array to force it to match the length of "to".
    if (to.length !== this.from.length) {
      // Force lengths to match
      this.from.length = to.length;
      if (to.length > this.from.length) {
        // "to" is longer than "from", so fill the empty elements in "from"
        this.from.fill(0, this.from.length);
      }
    }

    // Reset any previous animation before starting a new one
    this.stop();

    // Make note of the new target
    this.previousTo = to.slice();
    this.setTo(to);

    // Start the animation
    this.intervalID = setInterval(this.tick.bind(this), 50);
    this.startTime = Date.now();
  }

  /**
   * End the current animation.
   */
  stop () {
    clearInterval(this.intervalID);
    this.intervalID = -1;
  }

  /**
   * "Jumps" to a specific set of numbers without animating.
   *
   * @param to An array of numbers to skip to (e.g., [34, 0, 66]).
   */
  skipTo (to) {
    this.stop();
    this.setFrom(to.slice());
    this.setTo(to.slice());
    this.current = to.slice();
    this.previousTo = to.slice();
    this.dispatchEvent(CHANGE);
  }

  /**
   * Internal. Updates the position of the animation.
   */
  tick () {
    let now = Date.now();
    let elapsed = Math.min(this.duration, now - this.startTime);

    this.current = [];
    this.from.forEach((item, index) => {
      // The amount the animation has progressed so far. For example, if from[index]
      // is 10 and to[index] is 20, and 30% of the animation time has elapsed, then progress will be 3.
      let progress = Math.floor((this.to[index] - this.from[index]) * (elapsed/this.duration));
      // Current position is origin plus progress.
      this.current[index] = this.from[index] + progress;
    });

    // If the animation is finished...
    if (elapsed === this.duration) {
      // ...Advance the current transitioning numbers to their final destination
      this.current = [...this.to];

      // Start the next animation where the previous animation left off.
      // Use spread operator to copies values from this.current to a new array.
      this.setFrom([...this.current]);

      // Inform listeners that the animation has been updated
      this.dispatchEvent(CHANGE);

      // Clear the animation interval
      this.stop();
    } else {
      // Inform listeners that the animation has been updated
      this.dispatchEvent(CHANGE);
    }
  }

  /**
   * Indicates whether an animation is currently in progress.
   */
  isRunning () {
    return this.intervalID !== -1;
  }
}
